
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { wilayas } from '../data/mockData';

interface ProfileCompletionModalProps {
    onComplete: () => void;
    onClose?: () => void;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({ onComplete, onClose }) => {
    const { currentUser, updateUserProfile } = useAuth();
    const [phone, setPhone] = useState(currentUser?.phone || '');
    const [selectedWilaya, setSelectedWilaya] = useState(currentUser?.wilaya || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Sync with currentUser if data loads/changes while modal is open
    React.useEffect(() => {
        if (currentUser?.phone && !phone) setPhone(currentUser.phone);
        if (currentUser?.wilaya && !selectedWilaya) setSelectedWilaya(currentUser.wilaya);
    }, [currentUser]);

    // Safety check: if profile is already complete, don't show the modal
    if (currentUser?.phone && currentUser?.wilaya) {
        onComplete();
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!phone || !selectedWilaya) {
            setError('Veuillez remplir tous les champs.');
            return;
        }

        if (phone.length < 9) {
            setError('Numéro de téléphone invalide.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await updateUserProfile({
                phone,
                wilaya: selectedWilaya,
                needsProfileCompletion: false
            });
            onComplete();
        } catch (err: any) {
            setError(err.message || "Une erreur est survenue lors de l'enregistrement.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden animate-pop-in relative">
                {onClose && (
                    <button 
                        onClick={onClose}
                        className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all active:scale-95 z-10"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
                <div className="p-10">
                    <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                        <span className="text-3xl">🎉</span>
                    </div>

                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
                        Presque fini !
                    </h2>
                    <p className="text-center text-gray-600 mb-8 italic font-medium">
                        "Encore deux petits trucs et on y va !"
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2 block">
                                Votre numéro de téléphone
                            </label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">📞</span>
                                <input
                                    type="tel"
                                    placeholder="05 / 06 / 07 ..."
                                    value={phone}
                                    onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setPhone(val);
                        }}
                        maxLength={10}
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 focus:bg-white outline-none transition-all shadow-sm font-semibold"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2 block">
                                Votre Wilaya
                            </label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">📍</span>
                                <select
                                    value={selectedWilaya}
                                    onChange={(e) => setSelectedWilaya(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 focus:bg-white outline-none transition-all shadow-sm font-semibold appearance-none"
                                    required
                                >
                                    <option value="">Choisir ma ville...</option>
                                    {wilayas.map((w) => (
                                        <option key={w} value={w}>{w}</option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">
                                    ▼
                                </div>
                            </div>
                        </div>

                        {error && (
                            <p className="text-red-500 text-sm font-bold bg-red-50 p-3 rounded-xl border border-red-100 text-center animate-shake">
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-5 bg-red-600 hover:bg-red-700 text-white font-extrabold rounded-2xl shadow-lg shadow-red-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                        >
                            {isSubmitting ? 'Enregistrement...' : "C'est parti !"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProfileCompletionModal;
